import styles from "./addBulkOMS.module.scss";
import Dropdown from "../../../common/components/dropdown/Dropdown";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { updateIsLoading } from "../../../reducerSlices/loaderSlice";
import { useDispatch } from "react-redux";
import { useAxios } from "../../../services/useAxios";
import {
  APIMethods,
  urlConstants,
} from "../../../common/constants/urlConstants";
import InputField from "../../../common/components/formFields/inputField/inputField";
import Button from "../../../common/components/button/Button";
import { constants } from "../../../common/constants/constants";
import { ToastNotification } from "../../../common/components/toastNotification/ToastNotification";
import errorToast from "../../../assets/images/errorToast.svg";
import successToast from "../../../assets/images/successToast.svg";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import * as XLSX from "xlsx";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { getOMSProductFromBaseStructure } from "./addBulkOMS.helper";
import AbortService from "../../../services/abort";

const columns: GridColDef[] = [
  {
    field: "serial_number",
    headerName: "Index",
    width: 130,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "product_name",
    headerName: "Product Name",
    width: 160,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "unit_price",
    headerName: "Price",
    width: 160,
    sortable: false,
    disableColumnMenu: true,
  },
];

const AddBulkOMS = () => {
  const dispatch = useDispatch();
  const abortController = AbortService.getInstance();
  const [inputFocused, setInputFocused] = useState(false);
  const [supplierList, setSupplierList] = useState<any>([]);
  const [buyerList, setBuyerList] = useState<Record<string, any>[]>([]);
  const [toastData, setToastData] = useState(constants.defaultToastData);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>("");
  const [selectedSupplierName, setSelectedSupplierName] = useState<string>("");
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>("");
  const [selectedBuyerName, setSelectedBuyerName] = useState<string>("");
  const [loading, setLoading] = useState<any>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ----------------------  Form Validation Schema ----------------------

  const schema = yup.object().shape({
    buyerSearchName: yup.string(),
    supplierId: yup.string().required("Select Supplier"),
    buyerId: yup.string().required("Select Buyer"),
    parsedData: yup.array(),
    file: yup
      .mixed()
      .required("Please upload a file")
      .test(
        "fileType",
        "Unsupported File Format. Please upload a .csv",
        (value: any) => {
          if (!value || value.length === 0) return false; // No file selected
          const supportedFormats = [".csv", "text/csv"];
          return supportedFormats.includes(value[0]?.type);
        }
      ),
  });

  type FORM_TYPE = yup.InferType<typeof schema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<FORM_TYPE>({
    resolver: yupResolver(schema),
  });

  // ----------------------  Fetch Supplier List ----------------------

  const getSupplierDetails = useAxios({
    axiosParams: {
      url: `store/offlinesupplier/supplierlist/${selectedBuyerId}?catalogue_status=&is_active=&page=1&limit=1000&period_order_key=&period_order_value=&q=`,
    },
    method: APIMethods.get,
  });

  const getSupplierList = async () => {
    dispatch(updateIsLoading(true));
    const response: any = await getSupplierDetails.fetchData();
    dispatch(updateIsLoading(false));
    if (response && response?.status === "SUCCESS") {
      const businessData = response?.data.offline_suppliers;
      if (businessData && businessData?.length > 0) {
        const updatedBusiness = businessData?.map((element: any) => {
          return {
            id: element?.business_id,
            label: element?.business_name,
            value: element?.business_name,
            name: element?.business_name,
          };
        });
        if (updatedBusiness?.length > 0) {
          setSupplierList(updatedBusiness);
        }
      }
    }
  };

  useEffect(() => {
    getSupplierList();
  }, [selectedBuyerId]);

  // ----------------------  Buyer Search Functionality ----------------------

  const SearchBuyerByName = async (searchText: string) => {
    try {
      if (searchText.trim()) {
        const getBuyersListByName: any = await fetch(
          `${urlConstants.BASE_URL}store/buyer?q=${searchText}&page=1`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            signal: abortController.getSignal("getBuyersListByName"),
          }
        );

        const getBuyersListByNameData = await getBuyersListByName.json();

        if (
          getBuyersListByNameData &&
          getBuyersListByNameData?.status === "SUCCESS"
        ) {
          const businessData = getBuyersListByNameData?.data;
          if (businessData && businessData?.length > 0) {
            const updatedBusiness = businessData?.map((element: any) => {
              return {
                id: element?.id,
                name: element?.business_name,
              };
            });
            if (updatedBusiness?.length > 0) {
              setBuyerList(updatedBusiness);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
      } else {
        console.error("Fetch error:", error);
      }
    }
  };

  const onBuyerSearch = (searchText: string) => {
    setValue("buyerSearchName", searchText);
    SearchBuyerByName(searchText);
  };

  // ----------------------  File Upload Functionality ----------------------

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      dispatch(updateIsLoading(true));
      setLoading(true);

      try {
        // Read file as array buffer
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          throw new Error("File is empty");
        }

        const headers = (jsonData[0] as string[]).map((h) =>
          h.toString().toLowerCase().trim()
        );

        const productNameIndex = headers.findIndex(
          (h) => h.includes("product") && h.includes("name")
        );
        const unitPriceIndex = headers.findIndex(
          (h) =>
            (h.includes("unit") && h.includes("price")) ||
            h.includes("price") ||
            h.includes("cost")
        );

        if (productNameIndex === -1) {
          throw new Error(
            'Product name column not found. Expected column containing "product name"'
          );
        }

        if (unitPriceIndex === -1) {
          throw new Error(
            'Unit price column not found. Expected column containing "unit price" or "price"'
          );
        }

        const extractedData = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];

          if (row && row.length > Math.max(productNameIndex, unitPriceIndex)) {
            const productName = row[productNameIndex]?.toString().trim();
            const unitPriceValue = row[unitPriceIndex];

            // Convert price to number
            let unitPrice: number;
            if (typeof unitPriceValue === "number") {
              unitPrice = unitPriceValue;
            } else {
              // Remove currency symbols and parse
              const cleanPrice = unitPriceValue
                ?.toString()
                .replace(/[₹$£€,\s]/g, "")
                .trim();
              unitPrice = parseFloat(cleanPrice);
            }

            if (productName && !isNaN(unitPrice) && unitPrice > 0) {
              extractedData.push({
                id: crypto.randomUUID(),
                serial_number: i,
                product_name: productName,
                unit_price: unitPrice,
                row_number: i + 1,
              });
            }
          }
        }
        if (extractedData.length > 100) {
          setValue("file", "");
          setParsedData([]);
          setValue("parsedData", []);
          throw new Error("Too many rows extracted. Please limit to 100.");
        }

        if (extractedData.length === 0) {
          setValue("file", "");
          setParsedData([]);
          setValue("parsedData", []);
          trigger("file");
          throw new Error(
            "No valid product data found. Please check your file format."
          );
        }
        console.log("Extracted data:", extractedData);

        setValue("file", files);
        setParsedData(extractedData);
        setValue("parsedData", extractedData);
        trigger("file");

        setToastData({
          message: `File "${file.name}" processed successfully. Found ${extractedData.length} products.`,
          status: "Success",
        });
      } catch (error) {
        console.error("File processing error:", error);
        setToastData({
          message:
            error instanceof Error ? error.message : "Failed to process file",
          status: "Fail",
        });
        setParsedData([]);
      } finally {
        setTimeout(() => {
          setToastData(constants.defaultToastData);
        }, 3000);
        dispatch(updateIsLoading(false));
        setLoading(false);
      }
    }
  };

  // ----------------------  Form Reset Functionality ----------------------

  const resetForm = () => {
    reset();
    setParsedData([]);
    setSelectedBuyerId("");
    setSelectedBuyerName("");
    setSelectedSupplierId("");
    setValue("buyerSearchName", "");
    setBuyerList([]);
    setSupplierList([]);
  };

  // ----------------------  Form Submission Functionality ----------------------

  const onSubmit = async (data: FORM_TYPE) => {
    console.info("🚀 ~ onSubmit ~ data:", data);
    dispatch(updateIsLoading(true));

    try {
      if (!data.parsedData || data.parsedData.length === 0) {
        throw new Error("No parsed data to submit");
      }

      const omsProducts = data.parsedData.map((item) =>
        getOMSProductFromBaseStructure({
          title: item.product_name,
          unit_price: item.unit_price,
        })
      );

      const payload = {
        offline_products: omsProducts,
      };
      console.log("🚀 ~ onSubmit ~ payload:", payload);

      const masterOfflineProductId =
        process.env.REACT_APP_ENV_NAME === "production"
          ? "pro_01KAGK7J3D5ACQPA7SJJ66VTAD"
          : "pro_01K9728H9GB69K401S41PA00DE";
      const requestUrl = `${urlConstants.BASE_URL}store/offline-product/${masterOfflineProductId}/offline-supplier/${data.supplierId}/bulk`;

      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setToastData({
          message:
            "Products uploaded successfully to OMS system. Please allow some time for the products to reflect in the system.",
          status: "Success",
        });
        dispatch(updateIsLoading(false));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setToastData({
        message: "Upload failed. Please try again.",
        status: "Fail",
      });
    } finally {
      setTimeout(() => {
        setToastData(constants.defaultToastData);
      }, 10000);
      resetForm();
      dispatch(updateIsLoading(false));
    }
  };

  // ----------------------  JSX Return ----------------------

  return (
    <>
      <div className={styles.reportContainer}>
        <section className={styles.tableFilterContainer}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.filteSection}>
              <ClickAwayListener
                onClickAway={() => {
                  setInputFocused(false);
                }}
              >
                <div className={`${styles.input} ${styles.productNameSugg}`}>
                  <div className={styles.label}>Search Buyer</div>
                  <InputField
                    value={watch("buyerSearchName")}
                    placeholder="Enter Buyer Name"
                    id="buyerSearchName"
                    onChange={(e: any) => onBuyerSearch(e)}
                    register={register("buyerSearchName")}
                    maxWidth="100%"
                    errorMessage={errors.buyerSearchName?.message}
                    autoComplete={false}
                    onFocus={() => {
                      setInputFocused(true);
                    }}
                    tabIndex={1}
                  />
                  {inputFocused && buyerList?.length > 0 && (
                    <div className={styles.productNameSuggestionsContaine}>
                      {buyerList?.map((each_buyer: any, index: number) => (
                        <div
                          key={each_buyer.id}
                          onClick={() => {
                            setSelectedBuyerName(each_buyer.name);
                            setSelectedBuyerId(each_buyer.id);
                            setValue("buyerId", each_buyer.id);
                            setInputFocused(false);
                          }}
                          className={`${styles.productSuggestionItem} ${
                            index !== buyerList.length - 1
                              ? styles.seperator
                              : ""
                          }`}
                        >
                          {each_buyer.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ClickAwayListener>

              <div className={styles.input}>
                <div className={styles.label}>Selected Buyer</div>
                <InputField
                  value={selectedBuyerName}
                  placeholder="Selected Buyer"
                  id="selectedBuyer"
                  maxWidth="100%"
                  tabIndex={2}
                  disabled
                />
                {errors.buyerId && (
                  <div className={styles.errorMessage}>
                    {errors.buyerId.message}
                  </div>
                )}
              </div>

              <div className={styles.input}>
                <div className={styles.label}>Offline Suppliers</div>
                <Dropdown
                  minWidth="100%"
                  label={""}
                  selectedValue={selectedSupplierId}
                  options={supplierList}
                  defaultLabel={"Select Supplier"}
                  id="supplier"
                  ableToSelectSameValueFromDropdown={true}
                  onChange={(selectedOption: any) => {
                    if (typeof selectedOption === "string") {
                      setValue("supplierId", selectedOption);
                    } else {
                      setValue("supplierId", selectedOption.id);
                    }
                    setSelectedSupplierName(selectedOption.name);
                    setSelectedSupplierId(selectedOption.id);
                  }}
                  tabIndex={3}
                  searchField={true}
                  itemStyles={{ maxWidth: 300 }}
                />
              </div>

              <div className={styles.input}>
                <div className={styles.label}>Selected Supplier</div>
                <InputField
                  value={selectedSupplierName}
                  placeholder="Selected Supplier"
                  id="selectedSupplier"
                  maxWidth="100%"
                  tabIndex={4}
                  disabled
                />
                {errors.supplierId && (
                  <div className={styles.errorMessage}>
                    {errors.supplierId.message}
                  </div>
                )}
              </div>

              <div className={styles.buttonSection}>
                <div>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      label="Choose File"
                      id="choose-file-button"
                      handleClick={handleFileUploadClick}
                      large={false}
                      type="button"
                    />
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      {watch("file") && (watch("file") as FileList).length > 0
                        ? (watch("file") as FileList)[0].name
                        : "No file selected"}
                    </span>
                  </div>
                  {errors.file && (
                    <div className={styles.errorMessage}>
                      {errors.file.message}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    type="file"
                    multiple={false}
                    accept=".csv"
                    onChange={onFileUpload}
                    id="file-input"
                  />
                </div>
                <div className={styles.actions}>
                  <div>
                    <Button
                      label="Submit"
                      handleClick={() => {}}
                      width="160px"
                      type="submit"
                      customClass={styles.submit}
                    />
                  </div>
                  <div>
                    <Button
                      label="Reset"
                      handleClick={resetForm}
                      width="160px"
                      type="button"
                      customClass={styles.submit}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </section>
        <section className={styles.tableContainer}>
          <DataGrid
            rows={parsedData}
            columns={columns}
            hideFooterPagination
            hideFooter
            getRowId={(row) => row.id}
            loading={loading}
            rowCount={parsedData.length}
            sx={{
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              ".MuiDataGrid-main": {
                backgroundColor: "#fff",
              },
            }}
          />
        </section>
      </div>
      {toastData?.message && (
        <ToastNotification
          icon={toastData.status == "Success" ? successToast : errorToast}
          content={toastData?.message}
          position={"top-right"}
          autoClose={2000}
        />
      )}
    </>
  );
};
export default AddBulkOMS;
